import { ApiQueryName } from "@/models/api"
import { Lab } from "@/models/lab"
import { useRenameQueryResult } from "@/services/utils"
import { createQueryKey } from "@/services/utils/createQueryKey"
import type { HttpError } from "@/utils/api"
import { toHttpError } from "@/utils/api"
import { resolveQueryThrowOnError } from "@/utils/queryClient"
import { useQuery } from "@tanstack/react-query"
import type { CollectionProxy } from "spraypaint/lib-esm/proxies"

// LabResource refuses a larger page. Walk every page so a catalog past 100
// is not silently dropped.
const LABS_PAGE_SIZE = 100
const MAX_LAB_PAGES = 100

const getLabs = async (): Promise<Lab[]> => {
  try {
    const labs: Lab[] = []

    for (let page = 1; page <= MAX_LAB_PAGES; page += 1) {
      const response: CollectionProxy<Lab> = await Lab.per(LABS_PAGE_SIZE)
        .page(page)
        .all()

      labs.push(...response.data)

      if (response.data.length < LABS_PAGE_SIZE) {
        return labs
      }
    }

    throw new Error("Labs catalog exceeded the page cap")
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

export const useGetLabs = () => {
  const queryKey = createQueryKey(ApiQueryName.Labs, "getMany", null)
  const queryResult = useQuery<Lab[], HttpError>({
    queryFn: () => getLabs(),
    queryKey,
    throwOnError: resolveQueryThrowOnError(true),
  })

  return useRenameQueryResult(queryResult, ApiQueryName.Labs)
}
